import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createClubSchema, suggestShortCode, type ClubDto, type CreateClubInput } from "@tc/shared";
import { Button, Card, ClubAvatar, Field, Icon, Modal, Select, Tag, TextInput } from "@/components/ui";
import { Can } from "@/lib/auth/authz";
import { useClubs } from "@/lib/clubs/clubs";
import { adminApi, ApiError } from "./api";

/**
 * Back-office clubs administration (TW-3436). Lists every club and lets a `club.manage` holder
 * create one; clicking a row opens the club detail page (view → edit). The list is shared with the
 * topbar switcher via ClubsProvider, so a create updates both.
 */
export function ClubsPage() {
  const { t } = useTranslation();
  const { clubs, loading, refresh } = useClubs();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  return (
    <div className="page">
      <div className="page-head row-between">
        <div>
          <h1>{t("clubs.title")}</h1>
          <div className="sub">{t("clubs.sub")}</div>
        </div>
        <Can permission="club.manage">
          <Button icon="plus" onClick={() => setCreating(true)}>
            {t("clubs.newClub")}
          </Button>
        </Can>
      </div>

      {loading ? (
        <div className="empty">{t("common.comingSoon")}</div>
      ) : clubs.length === 0 ? (
        <div className="empty">
          <div className="empty-ic">
            <Icon name="building" size={24} />
          </div>
          {t("clubs.empty")}
        </div>
      ) : (
        <Card pad={false} style={{ overflow: "hidden" }}>
          <table className="table" style={{ margin: "8px 6px" }}>
            <thead>
              <tr>
                <th>{t("clubs.colClub")}</th>
                <th>{t("clubs.shortCode")}</th>
                <th>{t("clubs.status")}</th>
                <th>{t("users.title")}</th>
                <th>{t("clubs.colTrips")}</th>
                <th>{t("clubs.colOpen")}</th>
                <th>{t("clubs.since")}</th>
              </tr>
            </thead>
            <tbody>
              {clubs.map((club) => (
                <tr key={club.id} onClick={() => navigate(`/clubs/${club.id}`)} style={{ cursor: "pointer" }}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <ClubAvatar name={club.name} seed={club.id} />
                      <div className="t-trip">
                        <b>{club.name}</b>
                        <small>{club.slug}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="mono">{club.shortCode}</span>
                  </td>
                  <td>
                    <ClubStatusPill status={club.status} />
                  </td>
                  <td>
                    <span className="mono">{club.userCount}</span>
                  </td>
                  <td>
                    <span className="mono">{club.tripCount}</span>
                  </td>
                  <td>
                    {club.openTripCount > 0 ? (
                      <span className="mono" style={{ fontWeight: 650 }}>
                        {club.openTripCount}
                      </span>
                    ) : (
                      <span className="mono faint">0</span>
                    )}
                  </td>
                  <td className="mono muted" style={{ fontSize: 13 }}>
                    {new Date(club.createdAt).getFullYear()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {creating && (
        <ClubCreateModal
          onClose={() => setCreating(false)}
          onSaved={async (id) => {
            setCreating(false);
            await refresh();
            navigate(`/clubs/${id}`);
          }}
        />
      )}
    </div>
  );
}

function ClubStatusPill({ status }: { status: ClubDto["status"] }) {
  const { t } = useTranslation();
  const ok = status === "active";
  return (
    <Tag variant={ok ? "success" : "warning"} dot>
      {t(ok ? "clubs.statusActive" : "clubs.statusSuspended")}
    </Tag>
  );
}

interface ClubCreateProps {
  onClose: () => void;
  onSaved: (id: string) => void | Promise<void>;
}

function ClubCreateModal({ onClose, onSaved }: ClubCreateProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [shortCodeTouched, setShortCodeTouched] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useState<CreateClubInput["defaultLanguage"]>("es");
  const [status, setStatus] = useState<NonNullable<CreateClubInput["status"]>>("active");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The short code is auto-suggested from the name until the operator edits it themselves.
  function onName(v: string) {
    setName(v);
    if (!shortCodeTouched) setShortCode(v.trim() ? suggestShortCode(v) : "");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const parsed = createClubSchema.safeParse({ name, slug, shortCode, defaultLanguage, status });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("clubs.saveError"));
      return;
    }

    setSaving(true);
    try {
      const created = await adminApi.createClub(parsed.data as CreateClubInput);
      await onSaved(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("clubs.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t("clubs.newClub")}>
      <form onSubmit={onSubmit}>
        <div className="modal-body">
          {error && (
            <div className="auth-alert" style={{ marginBottom: 16 }} role="alert">
              <Icon name="alert" size={16} className="aa-ic" />
              <div className="aa-body">{error}</div>
            </div>
          )}
          <div className="admin-form">
            <Field label={t("clubs.name")} required>
              <TextInput value={name} onChange={onName} autoFocus />
            </Field>
            <Field label={t("clubs.slug")} required hint={t("clubs.slugHint")}>
              <TextInput value={slug} onChange={setSlug} placeholder="demo-club" />
            </Field>
            <Field label={t("clubs.shortCode")} required hint={t("clubs.shortCodeHint")}>
              <TextInput
                value={shortCode}
                onChange={(v) => {
                  setShortCodeTouched(true);
                  setShortCode(v);
                }}
              />
            </Field>
            <Field label={t("clubs.defaultLanguage")}>
              <Select
                value={defaultLanguage}
                onChange={(v) => setDefaultLanguage(v as CreateClubInput["defaultLanguage"])}
                options={[
                  { value: "es", label: "Español" },
                  { value: "en", label: "English" },
                ]}
              />
            </Field>
            <Field label={t("clubs.status")}>
              <Select
                value={status}
                onChange={(v) => setStatus(v as NonNullable<CreateClubInput["status"]>)}
                options={[
                  { value: "active", label: t("clubs.statusActive") },
                  { value: "suspended", label: t("clubs.statusSuspended") },
                ]}
              />
            </Field>
          </div>
        </div>
        <div className="modal-foot">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button type="submit" disabled={saving}>
            {t("clubs.create")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
